/**
 * Cola de publicaciones (§10.3).
 *
 * De momento lista, sin calendario. El calendario semanal y el arrastrar para
 * reprogramar son de la misma pantalla en el blueprint, pero sin ellos esto ya
 * sirve para lo que hace falta hoy: ver qué salió, qué está en curso y qué
 * requiere que alguien intervenga.
 *
 * Lo que **no** se hace es ordenar por fecha y ya. Lo que pide acción va arriba:
 * una publicación fallida entre veinte correctas no se puede quedar enterrada en
 * una lista cronológica.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AppShell } from '../components/app-shell.tsx';
import { type PublicationStatus, publicationsApi } from '../lib/api.ts';

const LABEL: Record<PublicationStatus, string> = {
  scheduled: 'Programada',
  processing: 'En curso',
  completed: 'Completada',
  partial: 'Parcial',
  failed: 'Fallida',
  canceled: 'Cancelada',
};

const TONE: Record<PublicationStatus, string> = {
  scheduled: 'bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]',
  processing: 'bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]',
  completed: 'bg-[var(--color-success-soft)] text-[var(--color-success)]',
  partial: 'bg-[var(--color-warning-soft)] text-[var(--color-warning)]',
  failed: 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
  canceled: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
};

/**
 * Cuánto "reclama atención" cada estado. Menor va primero.
 *
 * Fallida y parcial arriba porque son las únicas que necesitan que una persona
 * haga algo. Lo demás se lee en orden de fecha.
 */
const URGENCY: Record<PublicationStatus, number> = {
  failed: 0,
  partial: 1,
  processing: 2,
  scheduled: 3,
  completed: 4,
  canceled: 5,
};

export function PublicationsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['publications'],
    queryFn: publicationsApi.list,
    // Esta pantalla no usa SSE: son muchas publicaciones y abrir un flujo por
    // cada una no tiene sentido. Se refresca cada pocos segundos, que para una
    // lista es suficiente; el detalle sí va en vivo.
    refetchInterval: 10_000,
  });

  const publications = [...(data?.publications ?? [])].sort((a, b) => {
    const byUrgency = URGENCY[a.status] - URGENCY[b.status];
    return byUrgency !== 0 ? byUrgency : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <AppShell title="Publicaciones">
      {isLoading ? <p className="text-sm text-[var(--color-muted)]">Cargando…</p> : null}

      {!isLoading && publications.length === 0 ? (
        <div className="rounded-xl border border-[var(--color-line)] bg-white p-6 text-center">
          <p className="mb-3 text-sm text-[var(--color-muted)]">Todavía no has publicado nada.</p>
          <Link to="/redactar" className="text-sm text-[var(--color-brand)] underline">
            Redactar la primera noticia
          </Link>
        </div>
      ) : null}

      {publications.length > 0 ? (
        <ul className="divide-y divide-[var(--color-line)] rounded-xl border border-[var(--color-line)] bg-white">
          {publications.map((publication) => (
            <li key={publication.id}>
              <Link
                to="/publicaciones/$id"
                params={{ id: publication.id }}
                className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-[var(--color-surface)]"
              >
                <span className={`rounded-full px-2 py-0.5 text-xs ${TONE[publication.status]}`}>
                  {LABEL[publication.status]}
                </span>

                <span className="min-w-0 flex-1 truncate font-display text-base">
                  {publication.articleTitle}
                </span>

                <span className="text-xs text-[var(--color-muted)]">
                  {publication.mode === 'scheduled' && publication.scheduledAt
                    ? new Date(publication.scheduledAt).toLocaleString()
                    : new Date(publication.createdAt).toLocaleString()}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </AppShell>
  );
}
